import {
  ArrowBack,
  CheckCircle,
  Checklist,
  CloudUpload,
  ErrorOutline,
  ExpandMore,
  Lock,
  Receipt,
  Search,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { isUploadAllowed, isUploadEnabled, isCashPayment } from "./constants";
import {
  resolveDefaultTab,
  mapFetchFinalBillRow,
  readLensApplicableStatus,
  buildLensApplicableStatusPayload,
  isLensLocked,
  getStatusClass,
  isFilled,
} from "./pureLogic";
import * as api from "./billCollection.service";
import CentralLensModal from "../../../central/component/CentralLensDetails/CentralLensModal";
import DocumentUploadTab from "./DocumentUploadTab";
import DocumentActionsTab from "./DocumentActionsTab";
import "./styles.css";

const isViewerOnly = () => localStorage.getItem("viewer") === "true";

const getUser = () => {
  const userObj = JSON.parse(localStorage.getItem("user") || "{}") || {};
  return {
    userId: userObj?.userId || localStorage.getItem("userId") || "",
    userName:
      userObj?.username || userObj?.name || localStorage.getItem("userName") || "",
  };
};

/**
 * Combined Bill & Collection Actions — inline Accordion on the patient page.
 * The content mirrors central_dashboard's `BillCollectionModalComponent`.
 *
 * Nothing the user does on the Upload / Actions tabs is saved until
 * "Send for Approval" is confirmed. Everything is staged in memory and committed
 * together by `commitPendingChanges()`:
 *   1. staged upload      -> uploadFinalBillCentral (+ updateInsDashboard)
 *   2. staged decisions   -> updateOverallStatus (all documents in ONE call)
 *   3. then the normal Send for Approval save (saveCollectionEntry)
 *
 * API calls fire ONLY when the accordion is first expanded (lazy load).
 */
const CombinedBillCollection = ({
  data: dataProp,
  onRefresh,
  onUpdateIpdNo,
  onUpdateInfo,
  onClose,
}) => {
  const reduxPatientInfo = useSelector(
    (state) => state.patients?.patientInfo?.allInfo
  );
  const data = dataProp || reduxPatientInfo || {};

  const [expanded, setExpanded] = useState(false);
  const hasLoadedOnce = useRef(false);

  const [activeMainTab, setActiveMainTab] = useState("upload");
  const [finalImgArray, setFinalImgArray] = useState([]);
  const [disableFileUpload, setDisableFileUpload] = useState(false);
  const [isDisable, setIsDisable] = useState(false);
  const [isDetailedBillRequired, setIsDetailedBillRequired] = useState(false);

  // ── Lens gate (driven entirely by getFinalBillCentral) ──
  const [isLensApplicable, setIsLensApplicable] = useState(false);
  const [lensApplicableStatus, setLensApplicableStatus] = useState("");
  const [lensDetails, setLensDetails] = useState([]);
  const [lensChoice, setLensChoice] = useState(""); // "" | "Applicable" | "Not Applicable"
  const [lensSaved, setLensSaved] = useState(false);
  const [lensModalOpen, setLensModalOpen] = useState(false);
  const lensCountBeforeModal = useRef(0);

  const [loading, setLoading] = useState(true);
  const [searchAppId, setSearchAppId] = useState("");
  const [searching, setSearching] = useState(false);
  const [currentData, setCurrentData] = useState(data);

  // ── Upload tab entry fields (entry-only, always start empty) ──
  const [uploadFinalBillAmount, setUploadFinalBillAmount] = useState("");
  const [uploadNetBillAmount, setUploadNetBillAmount] = useState("");

  // ── Live amounts shared by the Actions tab ──
  const [finalBillAmount, setFinalBillAmount] = useState(null);
  const [netBillAmount, setNetBillAmount] = useState(null);

  // ── Deferred (staged) changes ──
  // uploadDirty  : files added/removed but "Go to Document Collection Actions"
  //                not clicked yet (blocks the Actions tab).
  // uploadStaged : "Go to Document Collection Actions" clicked; the upload
  //                (images + amounts) waits to be committed.
  const [uploadDirty, setUploadDirty] = useState(false);
  const [uploadStaged, setUploadStaged] = useState(false);
  const [newUploadUrls, setNewUploadUrls] = useState([]);
  // Decisions taken in this session, keyed by document _id (or url).
  const [pendingDecisionMap, setPendingDecisionMap] = useState({});
  const lastNetBillSent = useRef(null);

  const collectionStatus =
    typeof currentData?.collectionStatus === "object"
      ? currentData.collectionStatus?.status
      : currentData?.collectionStatus || "";

  const finalBillCBStatus =
    currentData?.finalBillCBStatus || currentData?.finalBillObj?.finalBillCB || "";

  // Upload gate: allowed when the collection status is one of the allowed
  // statuses OR the FB-status override applies OR isCentralFBStatus !== Approved.
  const uploadAllowed = isUploadEnabled(
    collectionStatus,
    currentData?.isCentralFBStatus,
    currentData?.isFinanceFBStatus,
    finalBillCBStatus
  );
  // Header pill reflects the collection status alone (central parity).
  const collectionStatusAllowed = isUploadAllowed(collectionStatus);

  const isFinalBill = currentData?.tabKey === "Final Bill";
  const appId = currentData?.appID || "";
  const leadId = currentData?.leadId || currentData?.lead_id || "";

  const hasStagedChanges =
    uploadStaged || Object.keys(pendingDecisionMap).length > 0;
  const hasUnsavedChanges = uploadDirty || hasStagedChanges;

  // ── getFinalBillCentral: uploaded docs + statuses (called with a known appId) ──
  const loadDocs = useCallback(
    async (idOverride) => {
      const id = idOverride || appId;
      if (!id) {
        setLoading(false);
        return null;
      }
      setLoading(true);
      try {
        const res = (await api.getFinalBillCentral(id)) || {};
        const d = res?.data || {};
        const images = d.images || d.finalBillCentral?.images || [];
        setFinalImgArray(images);
        // These flags come back at the TOP LEVEL of the response.
        const lensDetailsList = Array.isArray(res.lensDetails) ? res.lensDetails : [];
        setIsDetailedBillRequired(res.isDetailedBillRequired === true);
        setIsLensApplicable(res.isLensApplicable === true);
        setLensApplicableStatus(readLensApplicableStatus(res.lensApplicableStatus));
        setLensDetails(lensDetailsList);
        if (res.isLensApplicable !== true) {
          setLensChoice("");
          setLensSaved(false);
        }
        // Merge the final-bill status flags onto currentData so the upload gate
        // can apply the override. Only set keys that are present.
        if (
          "isCentralFBStatus" in d ||
          "isFinanceFBStatus" in d ||
          "isInsFBStatus" in d ||
          "finalBillCBStatus" in d ||
          "finalBillObj" in d
        ) {
          setCurrentData((prev) => ({
            ...prev,
            ...("isCentralFBStatus" in d ? { isCentralFBStatus: d.isCentralFBStatus } : {}),
            ...("isFinanceFBStatus" in d ? { isFinanceFBStatus: d.isFinanceFBStatus } : {}),
            ...("isInsFBStatus" in d ? { isInsFBStatus: d.isInsFBStatus } : {}),
            ...("finalBillCBStatus" in d ? { finalBillCBStatus: d.finalBillCBStatus } : {}),
            ...("finalBillObj" in d ? { finalBillObj: d.finalBillObj } : {}),
          }));
        }
        if (d.notUploadFB === true) {
          setDisableFileUpload(true);
          toast.info("Insurance Has Approved the Final Bill");
        }
        if (d.isFinanceFBStatus === "Approved") {
          setIsDisable(true);
          toast.info("Finance Has Approved the Final Bill");
        }
        if (d.isInsFBStatus === "Approved") {
          setIsDisable(true);
          toast.info("Insurance Has Approved the Final Bill");
        }
        return { ...d, lensDetails: lensDetailsList };
      } catch (err) {
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [appId]
  );

  // Reset every piece of per-appointment state (central `rebuildFromRaw`).
  const resetState = () => {
    setFinalImgArray([]);
    setDisableFileUpload(false);
    setIsDisable(false);
    setIsDetailedBillRequired(false);
    setUploadDirty(false);
    setUploadStaged(false);
    setNewUploadUrls([]);
    setPendingDecisionMap({});
    lastNetBillSent.current = null;
    setUploadFinalBillAmount("");
    setUploadNetBillAmount("");
    setLensChoice("");
    setLensSaved(false);
  };

  // Seed the live amounts from the row, like central `initActionsTab`.
  const seedAmounts = (row) => {
    setFinalBillAmount(
      row?.finalBillObj?.finalBillAmount ?? row?.finalBillAmount ?? null
    );
    setNetBillAmount(
      row?.finalBillObj?.netBillAmount ?? row?.netBillAmount ?? null
    );
  };

  // First load: fetchFinalBill(appId) → rebuild the row, then getFinalBillCentral.
  const loadOnMount = useCallback(async () => {
    if (!appId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.fetchFinalBill(appId);
      const raw = res?.data?.[0];
      if (raw) {
        const rebuilt = mapFetchFinalBillRow(raw);
        setCurrentData(rebuilt);
        seedAmounts(rebuilt);
        await loadDocs(rebuilt.appID || appId);
        return;
      }
    } catch (err) {
      console.error(err);
    }
    seedAmounts(currentData);
    await loadDocs(appId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, loadDocs]);

  // Fire API calls ONLY on first expand (lazy load).
  useEffect(() => {
    if (expanded && !hasLoadedOnce.current) {
      hasLoadedOnce.current = true;
      loadOnMount();
    }
  }, [expanded, loadOnMount]);

  // ── Resolve default tab once loading resolves ──
  useEffect(() => {
    if (loading) return;
    setActiveMainTab(resolveDefaultTab(uploadAllowed, finalImgArray.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, uploadAllowed]);

  // Only mirror the sparse prop BEFORE the first load; afterwards the rebuilt
  // row must not be clobbered.
  useEffect(() => {
    if (dataProp && !hasLoadedOnce.current) setCurrentData(dataProp);
  }, [dataProp]);

  // ── Search / switch appointment ──
  const handleSearch = async () => {
    const query = searchAppId.trim();
    if (!query) return;
    if (query === appId) {
      toast.info("Already viewing this appointment");
      setSearchAppId("");
      return;
    }
    if (
      hasUnsavedChanges &&
      !window.confirm(
        "You have changes that are not saved yet (uploads / approvals / declines). They will be lost if you switch. Switch anyway?"
      )
    ) {
      return;
    }
    setSearching(true);
    try {
      const res = await api.fetchFinalBill(query);
      if (res?.data?.[0]) {
        const rebuilt = mapFetchFinalBillRow(res.data[0]);
        resetState();
        setCurrentData(rebuilt);
        seedAmounts(rebuilt);
        await loadDocs(rebuilt.appID || query);
        setSearchAppId("");
        toast.success("Switched to " + (rebuilt.appID || query));
      } else {
        toast.error("Appointment not found");
      }
    } catch {
      toast.error("Failed to fetch appointment");
    } finally {
      setSearching(false);
    }
  };

  // ───────────── Upload tab → Actions tab (NO API call, staging only) ─────────────
  const isCash = isCashPayment(currentData?.payment_mode);

  const isSubmitUploadDisabled = (() => {
    const hasDocs = finalImgArray && finalImgArray.length > 0;
    if (!hasDocs && !disableFileUpload) return true;
    if (isCash && (!isFilled(uploadFinalBillAmount) || !isFilled(uploadNetBillAmount))) {
      return true;
    }
    return false;
  })();

  const showUploadAmountNote =
    isCash && (!isFilled(uploadFinalBillAmount) || !isFilled(uploadNetBillAmount));

  // Stage a document that was just uploaded (in storage, NOT yet in the DB).
  const handleFilesAdded = (docs) => {
    setFinalImgArray((prev) => [...prev, ...docs]);
    setNewUploadUrls((prev) => [...prev, ...docs.map((d) => d.url)]);
    setUploadDirty(true);
  };

  const handleDeleteFile = (index) => {
    const removed = finalImgArray[index];
    if (!removed) return;
    setFinalImgArray((prev) => prev.filter((_, i) => i !== index));
    setUploadDirty(true);
    if (newUploadUrls.includes(removed.url)) {
      // never saved: just forget it
      setNewUploadUrls((prev) => prev.filter((u) => u !== removed.url));
      return;
    }
    // A saved document: drop it from the Actions list and from pending decisions.
    // The removal itself is saved on Send for Approval (via the staged upload).
    setCurrentData((prev) => ({
      ...prev,
      urlArray: (prev.urlArray || []).filter((x) => {
        const same = (removed._id && x._id === removed._id) || x.url === removed.url;
        if (same) {
          setPendingDecisionMap((m) => {
            const copy = { ...m };
            delete copy[x._id || x.url];
            return copy;
          });
        }
        return !same;
      }),
    }));
  };

  // "Go to Document Collection Actions". NO API is called here.
  const goToActionsFromUpload = () => {
    if (isViewerOnly()) {
      toast.warning("You are only allowed to view.");
      return;
    }
    if (!disableFileUpload && !(finalImgArray && finalImgArray.length > 0)) {
      toast.info("No Image Uploaded");
      return;
    }
    if (isSubmitUploadDisabled) return;

    // Cash amounts are mandatory on this screen, so for Cash the upload step is
    // always committed on Send for Approval. For other modes only when the user
    // actually added / removed a file.
    if (uploadDirty || isCash) setUploadStaged(true);
    setUploadDirty(false);

    // Carry the amounts into Verification & Action so both screens agree.
    if (isCash) {
      setFinalBillAmount(uploadFinalBillAmount);
      setNetBillAmount(uploadNetBillAmount);
    }

    // Show the documents added on the Upload tab inside the Actions tab. They
    // are not saved yet (no _id), so Approve / Decline stay disabled for them.
    setCurrentData((prev) => {
      const current = prev.urlArray || [];
      const base = current.filter((x) => !x.isStaged || x.committed);
      const staged = (finalImgArray || [])
        .filter((img) => newUploadUrls.includes(img.url))
        .map((img) => ({
          url: img.url,
          name: img.name,
          dateTimeISO: img.dateTimeISO,
          user: img.user,
          userName: img.userName,
          isStaged: true,
        }));
      return { ...prev, urlArray: base.concat(staged) };
    });
    setActiveMainTab("actions");
  };

  // Top "Document / Collection Actions" tab click.
  const openActionsTab = () => {
    if (uploadDirty) {
      toast.info(
        'Please click "Go to Document Collection Actions" to continue with your upload changes.'
      );
      return;
    }
    setActiveMainTab("actions");
  };

  // ─────────────── Commit on "Send for Approval" ───────────────

  // updateInsDashboard with the Net Bill Amount. Cash only, only when an amount
  // exists, and skipped when the same amount was already pushed. Non-blocking.
  const pushNetBill = async (amount) => {
    if (!isCash || !isFilled(amount)) return;
    const n = Number(amount);
    if (lastNetBillSent.current === n) return;
    const { userId, userName } = getUser();
    try {
      await api.updateInsDashboard({ netBillAmount: n, userId, userName, appId });
      lastNetBillSent.current = n;
    } catch {
      /* don't block the flow on failure */
    }
  };

  const commitUpload = async () => {
    if (!uploadStaged) return true;
    if (isViewerOnly()) {
      toast.warning("You are only allowed to view.");
      return false;
    }
    const reqObj = {
      app_id: appId,
      finalBillCentral: { images: finalImgArray },
    };
    // Use the live amounts (they may have been edited in Verification & Action).
    const finalAmt = isFilled(finalBillAmount) ? finalBillAmount : uploadFinalBillAmount;
    const netAmt = isFilled(netBillAmount) ? netBillAmount : uploadNetBillAmount;
    if (isCash) {
      reqObj.finalBillObj = { finalBillAmount: finalAmt, netBillAmount: netAmt };
      reqObj.isInsFBStatus = "Approved";
    }
    try {
      const res = await api.uploadFinalBillCentral(reqObj);
      if (res && res.status) {
        setUploadStaged(false);
        setNewUploadUrls([]);
        setCurrentData((prev) => ({
          ...prev,
          urlArray: (prev.urlArray || []).map((x) =>
            x.isStaged ? { ...x, committed: true } : x
          ),
        }));
        await pushNetBill(netAmt);
        return true;
      }
      toast.error("Something Went Wrong while uploading documents. Send for Approval was not done.");
      return false;
    } catch {
      toast.error("Failed to upload documents. Send for Approval was not done.");
      return false;
    }
  };

  // Persist ALL staged Approve / Decline decisions in ONE updateOverallStatus
  // call (central `persistDocumentDecisions`).
  const commitDecisions = async () => {
    const decisions = Object.keys(pendingDecisionMap).map((k) => pendingDecisionMap[k]);
    if (!decisions.length) return true;
    if (isViewerOnly()) {
      toast.warning("You are only allowed to view.");
      return false;
    }
    const { userId, userName } = getUser();
    const reqObj = {
      tab_name: currentData?.tab_name || "postdischarge",
      document_id: currentData?.document_id || currentData?.documentId || "",
      key: currentData?.key || "finalBill",
      app_id: appId,
      url_id: decisions,
      cb_objectId: currentData?.cb_objectId || "",
      user_id: userId,
      user_name: userName,
      source: "Central Dashboard",
    };
    if (isCash) {
      reqObj.finalBillObj = { finalBillAmount };
      reqObj.isInsFBStatus = "Approved";
    }
    try {
      const response = await api.updateOverallStatus(reqObj);
      if (response?.status === 200) {
        toast.success("Documents Status Updated Successfully");
        setPendingDecisionMap({});
        await pushNetBill(netBillAmount);
        return true;
      }
      toast.error(response?.message || "Something Went Wrong");
      return false;
    } catch {
      toast.error("Something Went Wrong");
      return false;
    }
  };

  // Called by Collection Updation right before it saves "Send for Approval".
  // Resolves false (Send for Approval is then NOT performed) if any step fails.
  // Each step clears its own staged data on success, so a retry only re-runs
  // what has not been saved yet.
  const commitPendingChanges = async () => {
    const ok = await commitUpload();
    return ok ? commitDecisions() : false;
  };

  // Keep a decision in memory and reflect it in the UI (central `stageDecision`).
  const stageDecision = (index, id, decision, statusLabel) => {
    setPendingDecisionMap((m) => ({
      ...m,
      [id || currentData?.urlArray?.[index]?.url]: decision,
    }));
    setCurrentData((prev) => {
      const urlArray = [...(prev.urlArray || [])];
      if (urlArray[index]) {
        urlArray[index] = {
          ...urlArray[index],
          status: statusLabel,
          ...(decision.declineReason ? { declineReason: decision.declineReason } : {}),
        };
      }
      return { ...prev, urlArray };
    });
  };

  // Collapses ONLY on a successful Send for Approval (central `onApprovalSuccess`).
  const handleApprovalSuccess = () => {
    setExpanded(false);
    onClose?.({ status: "approved" });
    onRefresh?.();
  };

  // Back arrow = collapse the section (the inline equivalent of closing central's
  // modal), with the same unsaved-changes confirmation.
  const closeModal = () => {
    if (
      hasUnsavedChanges &&
      !window.confirm(
        "You have changes that are not saved yet (uploads / approvals / declines). They will be lost if you close. Close anyway?"
      )
    ) {
      return;
    }
    setExpanded(false);
  };

  // ── Lens gate handlers (mirror the central dashboard) ──
  const persistLensChoice = async (value) => {
    try {
      const { userId } = getUser();
      const payload = buildLensApplicableStatusPayload(appId, value, userId);
      const res = await api.updateCentralDashReporting(payload);
      const ok = res?.status === 200 || res?.status === 1 || res?.message === "Success";
      if (!ok) toast.error("Could not save lens selection. Please try again.");
      return ok;
    } catch (err) {
      console.error(err);
      toast.error("Could not save lens selection. Please try again.");
      return false;
    }
  };

  const handleLensChoice = async (choice) => {
    setLensChoice(choice);
    setLensSaved(false);
    const ok = await persistLensChoice(choice);
    if (!ok) {
      setLensChoice(""); // revert so the tab stays locked; user can retry
      return;
    }
    if (choice === "Applicable") {
      lensCountBeforeModal.current = lensDetails.length;
      setLensModalOpen(true);
    }
  };

  // The reused CentralLensModal has no success callback; detect a successful
  // save by re-fetching and checking whether the lens details count increased.
  const handleLensModalClose = async () => {
    setLensModalOpen(false);
    const d = await loadDocs();
    const newCount = Array.isArray(d?.lensDetails) ? d.lensDetails.length : 0;
    if (newCount > lensCountBeforeModal.current) {
      setLensSaved(true);
      toast.success("Lens details saved. You can now proceed with the documents.");
    } else {
      setLensSaved(false);
      toast.error("Lens details not saved. Please enter lens details to continue.");
    }
  };

  const lensLocked = isLensLocked({
    isLensApplicable,
    lensApplicableStatus,
    lensChoice,
    lensSaved,
  });

  const uploadedCount = finalImgArray ? finalImgArray.length : 0;

  const statusPills = [
    { label: "Central FB", value: currentData?.isCentralFBStatus },
    { label: "Finance FB", value: currentData?.isFinanceFBStatus },
    { label: "Final Bill CB", value: finalBillCBStatus },
  ].filter((p) => p.value);

  return (
    <Accordion
      className="cbc-accordion"
      expanded={expanded}
      onChange={(_e, isExpanded) => setExpanded(isExpanded)}
      sx={{
        boxShadow: "0px 4px 10px 0px #75757533 !important",
        background: "#fff",
        borderRadius: "4px !important",
        m: "0px 16px 10px",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ color: "#3476CD" }} />}
        sx={{ minHeight: "45px" }}
        aria-controls="combined-bill-collection-content"
      >
        <Box sx={{ width: "100%" }}>
          <Box className="cbc-accordion-title">
            <Receipt sx={{ fontSize: 16, color: "#3476CD" }} />
            <Typography
              component="h2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#3476CD",
                lineHeight: 1.25,
              }}
            >
              Combined Bill &amp; Collection Actions
            </Typography>
            {!expanded && collectionStatus && (
              <span
                className={`bcm-status-badge ${
                  collectionStatusAllowed ? "bcm-status-allowed" : "bcm-status-locked"
                }`}
              >
                {collectionStatusAllowed ? (
                  <CheckCircle sx={{ fontSize: 12 }} />
                ) : (
                  <Lock sx={{ fontSize: 12 }} />
                )}
                {collectionStatus}
              </span>
            )}
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ borderTop: "0.5px solid #3476CD33", p: 0 }}>
    <div className="bcm-root">
      {/* ══════════════════ MODAL HEADER ══════════════════ */}
      <div className="bcm-header">
        <div className="bcm-header-left">
          <button
            type="button"
            className="bcm-back"
            onClick={closeModal}
            title="Back to Home"
          >
            <ArrowBack sx={{ fontSize: 18 }} />
          </button>
          <h5 className="bcm-title">Bill &amp; Collection</h5>
          {appId && <span className="bcm-app-badge">appId: {appId}</span>}
          {leadId && <span className="bcm-lead-badge">leadId: {leadId}</span>}
          {collectionStatus && (
            <span
              className={`bcm-status-badge ${
                collectionStatusAllowed ? "bcm-status-allowed" : "bcm-status-locked"
              }`}
            >
              {collectionStatusAllowed ? (
                <CheckCircle sx={{ fontSize: 12 }} />
              ) : (
                <Lock sx={{ fontSize: 12 }} />
              )}
              {collectionStatus}
            </span>
          )}
        </div>
        <div className="bcm-header-center">
          <div className="bcm-search-box">
            <Search sx={{ fontSize: 15 }} className="bcm-search-icon" />
            <input
              type="text"
              className="bcm-search-input"
              placeholder="Switch by Appointment ID..."
              value={searchAppId}
              onChange={(e) => setSearchAppId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              aria-label="Switch by Appointment ID"
            />
            {searchAppId && (
              <button
                type="button"
                className="bcm-search-go"
                onClick={handleSearch}
                disabled={searching}
                aria-label="Search appointment"
              >
                {searching ? <CircularProgress size={12} /> : "→"}
              </button>
            )}
          </div>
        </div>
        <div className="bcm-header-actions">
          {hasUnsavedChanges && (
            <span
              className="bcm-unsaved-note"
              title="Uploads and Approve / Decline actions are saved when you click Send for Approval"
            >
              <ErrorOutline sx={{ fontSize: 12, mr: 0.5, verticalAlign: "text-bottom" }} />
              Unsaved changes. Complete Send for Approval
            </span>
          )}
        </div>
      </div>

      {/* ══════════════════ TOP TAB STRIP ══════════════════ */}
      <div className="bcm-top-tabs">
        <button
          type="button"
          className={`bcm-top-tab ${activeMainTab === "upload" ? "active" : ""} ${
            !uploadAllowed ? "bcm-tab-disabled" : ""
          }`}
          disabled={!uploadAllowed}
          title={!uploadAllowed ? "Document upload is locked for this collection status" : ""}
          onClick={() => uploadAllowed && setActiveMainTab("upload")}
        >
          {uploadAllowed ? (
            <CloudUpload sx={{ fontSize: 15 }} />
          ) : (
            <Lock sx={{ fontSize: 15 }} />
          )}
          Document Upload
          {uploadedCount > 0 && <span className="bcm-badge">{uploadedCount}</span>}
        </button>
        <button
          type="button"
          className={`bcm-top-tab ${activeMainTab === "actions" ? "active" : ""}`}
          onClick={openActionsTab}
        >
          <Checklist sx={{ fontSize: 15 }} />
          Document / Collection Actions
        </button>
        {statusPills.length > 0 && (
          <div className="bcm-status-badges-strip">
            {statusPills.map((p) => (
              <span className="bcm-badge-item" key={p.label}>
                {p.label}:{" "}
                <span className={`bcm-status-pill ${getStatusClass(p.value)}`}>
                  {p.value}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ══════════════════ TAB CONTENT ══════════════════ */}
      <div className="bcm-body">
        {loading ? (
          <div className="bcm-loading">
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            {activeMainTab === "upload" && !uploadAllowed && (
              <div className="bcm-upload-locked">
                <Lock className="bcm-locked-icon" />
                <span className="bcm-locked-title">Document upload is locked</span>
                <span className="bcm-locked-sub">
                  Uploads are allowed only when the collection status is
                  &quot;Pending at Central Dashboard&quot; or &quot;Collection
                  Rejected — Pending at Central Dashboard&quot;.
                </span>
                {collectionStatus && (
                  <span className="bcm-locked-status">
                    Current status: <strong>{collectionStatus}</strong>
                  </span>
                )}
              </div>
            )}

            {activeMainTab === "upload" && uploadAllowed && (
              <DocumentUploadTab
                data={currentData}
                finalImgArray={finalImgArray}
                disableFileUpload={disableFileUpload}
                uploadFinalBillAmount={uploadFinalBillAmount}
                setUploadFinalBillAmount={setUploadFinalBillAmount}
                uploadNetBillAmount={uploadNetBillAmount}
                setUploadNetBillAmount={setUploadNetBillAmount}
                showAmountNote={showUploadAmountNote}
                isSubmitDisabled={isSubmitUploadDisabled}
                onFilesAdded={handleFilesAdded}
                onDelete={handleDeleteFile}
                onSubmit={goToActionsFromUpload}
              />
            )}

            {activeMainTab === "actions" && (
              <DocumentActionsTab
                data={currentData}
                urlArray={currentData?.urlArray || []}
                isDisable={isDisable}
                isDetailedBillRequired={isDetailedBillRequired}
                isFinalBill={isFinalBill}
                finalBillAmount={finalBillAmount}
                setFinalBillAmount={setFinalBillAmount}
                netBillAmount={netBillAmount}
                setNetBillAmount={setNetBillAmount}
                pendingDecisionMap={pendingDecisionMap}
                onStageDecision={stageDecision}
                commitPendingChanges={commitPendingChanges}
                isLensApplicable={isLensApplicable}
                hasLensDecision={
                  !!(lensApplicableStatus && String(lensApplicableStatus).trim())
                }
                lensChoice={lensChoice}
                lensSaved={lensSaved}
                lensLocked={lensLocked}
                onLensChoice={handleLensChoice}
                onReopenLensModal={() => setLensModalOpen(true)}
                onRefresh={onRefresh}
                onUpdateIpdNo={onUpdateIpdNo}
                onUpdateInfo={onUpdateInfo}
                onGoToUpload={() => {
                  if (uploadAllowed) setActiveMainTab("upload");
                  else toast.info("Document upload is locked for this status");
                }}
                onStatusChange={(newStatus) => {
                  // Sync the freshly-saved collection status up so the header
                  // pill (and upload gating) reflect it immediately.
                  setCurrentData((prev) => ({ ...prev, collectionStatus: newStatus }));
                }}
                onApprovalSuccess={handleApprovalSuccess}
              />
            )}
          </>
        )}

        {/* Reused Reporting lens modal (no duplicate). Opened only via the
            lens gate when the user selects "Applicable". */}
        {lensModalOpen && (
          <CentralLensModal
            open={lensModalOpen}
            onClose={handleLensModalClose}
            onOpen={() => setLensModalOpen(true)}
            appointmentId={appId}
            sx={{ maxWidth: "720px", width: "90%" }}
          />
        )}
      </div>
    </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default CombinedBillCollection;
